import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  closeBusinessDay,
  closePostingPeriod,
  createGlAccount,
  ledgerKeys,
  openBusinessDay,
  postManualJournal,
  reopenBusinessDay,
  reopenPostingPeriod,
  resolveDriftAlert,
  reverseJournal,
  runDriftCheck,
  runProvisionClassification,
  updateGlAccount,
} from '@/api/ledger-api'
import type {
  CloseBusinessDayRequest,
  ClosePostingPeriodRequest,
  CreateGlAccountRequest,
  LedgerProvisionRunRequest,
  OpenBusinessDayRequest,
  PostManualJournalRequest,
  ReopenBusinessDayRequest,
  ReopenPostingPeriodRequest,
  ResolveDriftAlertRequest,
  ReverseJournalRequest,
  RunDriftCheckRequest,
  UpdateGlAccountRequest,
} from '@/api/ledger-types'

/**
 * The ledger's commands.
 *
 * Four of them are countersigned — a manual journal, a reversal, a period reopening, and the day
 * close — and every one of those answers with an envelope rather than with the thing that was asked
 * for. Nothing in this file interprets that envelope; the screens do, through
 * `LedgerDualControlOutcome`, because the interpretation is the message to the operator and it must
 * not be split between a hook and a panel.
 *
 * **Invalidation is deliberate rather than blanket.** A queued command changes nothing in the
 * ledger, so invalidating the journal list after one would replace correct rows with identical
 * correct rows and imply something had moved. Where a command may or may not have taken effect, the
 * key is invalidated either way and for a specific reason: even a queued request is worth seeing
 * reflected in whatever the approvals queue drives, and a stale list is a worse failure than an
 * unnecessary read.
 */

/**
 * Opens a branch's trading day.
 *
 * Invalidates the business-day key rather than writing the answer into the cache. The answer to
 * opening a day is an `OpenedBusinessDay` — a different shape from the `CurrentBusinessDay` the
 * header reads — and re-reading is what makes the header, this screen, and any other branch card
 * agree. The key is shared with `reference-api`, so the invalidation reaches all of them.
 */
export function useOpenBusinessDay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: OpenBusinessDayRequest) => openBusinessDay(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.businessDays() })
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.all })
    },
  })
}

/**
 * Closes a branch's trading day.
 *
 * The whole of the ledger goes stale on a close and that is not an exaggeration: the day's rollups
 * are finalised, the drift register may have gained alarms from the integrity check the close ran,
 * and the register's row for the day changes. The header's business date changes too, which is why
 * the shared `business-day` key is invalidated alongside.
 */
export function useCloseBusinessDay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CloseBusinessDayRequest) => closeBusinessDay(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.all })
    },
  })
}

/**
 * Reopens a branch's most recent closed day. **Countersigned.**
 *
 * The same blanket invalidation as the close, for the same reason run backwards: a reopened day
 * un-reports its figures, changes the header's business date, and puts the register's row back in
 * play. A queued request changes nothing, and the re-read is what proves that to the operator.
 */
export function useReopenBusinessDay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ReopenBusinessDayRequest) => reopenBusinessDay(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.all })
    },
  })
}

/**
 * Adds an account to the chart. **Countersigned** — the result may be a queued request.
 *
 * The chart's hour-long staleness is exactly why the invalidation is unconditional: on the created
 * path there is a new node to show, and on the queued path re-reading proves nothing appeared.
 */
export function useCreateGlAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateGlAccountRequest) => createGlAccount(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.glAccounts() })
    },
  })
}

/**
 * Renames, remaps, blocks, unblocks or archives an account. **Countersigned.**
 *
 * Blocking is the movement worth respecting in the cache: the manual-journal form reads the chart
 * to decide what may be posted to, so a blocked account must fall out of that read, not linger.
 */
export function useUpdateGlAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ code, request }: { code: string; request: UpdateGlAccountRequest }) =>
      updateGlAccount(code, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.glAccounts() })
    },
  })
}

/**
 * Raises a journal by hand. **Countersigned** — the result may be a queued request.
 *
 * The journal list is invalidated on either outcome. On the posted path there is a new entry to
 * show; on the queued path there is not, and re-reading proves it — a list that silently kept its
 * previous rows would leave the maker unable to tell the two apart by looking.
 */
export function usePostManualJournal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: PostManualJournalRequest) => postManualJournal(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.journals() })
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.trialBalances() })
    },
  })
}

/**
 * Reverses a posted entry. **Countersigned.**
 *
 * The original entry's cached detail is invalidated by hand: a completed reversal flips its
 * `status` to `Reversed` and fills in `reversedByEntryPublicId`, and that record is otherwise
 * cached forever on the grounds that a posted journal is immutable. This is the one thing that
 * changes it.
 */
export function useReverseJournal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      journalPublicId,
      request,
    }: {
      journalPublicId: string
      request: ReverseJournalRequest
    }) => reverseJournal(journalPublicId, request),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.journal(variables.journalPublicId) })
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.journals() })
    },
  })
}

/**
 * Runs classification and posts the provision movement.
 *
 * Not countersigned: it executes on the click. It posts a journal, so the journal list and the
 * balances behind the trial balance are both stale afterwards — including on a run that posted
 * nothing, where the invalidation costs one read and proves the book did not move.
 */
export function useRunProvisionClassification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: LedgerProvisionRunRequest) => runProvisionClassification(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.journals() })
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.all })
    },
  })
}

/**
 * Soft closes or seals an accounting period.
 *
 * Everything downstream of a period's state changes with it: what a manual journal may do, what the
 * trial balance is asserting about a settled month, and whether a day inside it can still close.
 */
export function useClosePostingPeriod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ClosePostingPeriodRequest) => closePostingPeriod(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.postingPeriods() })
    },
  })
}

/**
 * Reopens a closed period. **Countersigned.**
 *
 * The period list is invalidated even though a queued reopening leaves the period exactly as it
 * was. The re-read is what stops the screen showing a period as reopened on the strength of a
 * request nobody has signed.
 */
export function useReopenPostingPeriod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ReopenPostingPeriodRequest) => reopenPostingPeriod(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.postingPeriods() })
    },
  })
}

/**
 * Re-derives the daily rollups and alarms on any that disagree.
 *
 * The run's own findings come back in the response and are shown from it. The register is
 * invalidated as well, because a run raises alarms that belong in the list beside the ones already
 * there — and because the alarms it raises are what will block a day close.
 */
export function useRunDriftCheck() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RunDriftCheckRequest) => runDriftCheck(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.drift() })
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.dailyBalanceRollups() })
    },
  })
}

/**
 * Disposes of one drift alarm.
 *
 * Not countersigned — the answer is the alarm in its new state. Both the register and the daily
 * balances are invalidated: a `Repaired` disposition rewrites the stored rollup, so the balances
 * that were read a moment ago are no longer the ones in the book.
 */
export function useResolveDriftAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ alertId, request }: { alertId: string; request: ResolveDriftAlertRequest }) =>
      resolveDriftAlert(alertId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.drift() })
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.dailyBalanceRollups() })
    },
  })
}
